import ReviewConstants from 'notebook/reviews/ReviewConstants';

/**
 * The subset of an entry's editor state (`EntryStore.getEditorState(id)`) needed to decide editability.
 * Kept structural so it can be satisfied by the store's editor state or a plain object in tests.
 */
export interface EntryEditableState {
  // `entry` can be null while an entry id is registered but its payload is still loading.
  entry: {
    permissions: {writable?: boolean};
    review?: {status?: string} | null;
  } | null;
  isLatest: boolean;
  dirty: boolean;
}

const LOCKED_STATUSES: ReadonlyArray<string> = ReviewConstants.LOCKED_STATUSES;

/**
 * Single source of truth for "can the user edit this entry right now".
 *
 * Mirrors Entry.allowEdits() (Entry.js): writable && latest && !dirty && !locked-for-review.
 * Returns false when the entry payload is still null (e.g. during async open/load), since a
 * not-yet-loaded entry cannot be edited.
 */
export function canEditEntry(state: EntryEditableState): boolean {
  const {entry, isLatest, dirty} = state;
  if (entry == null) {
    return false;
  }
  const reviewStatus = entry.review?.status;
  const isLockedForReview = reviewStatus != null && LOCKED_STATUSES.includes(reviewStatus);
  return Boolean(entry.permissions.writable) && isLatest && !dirty && !isLockedForReview;
}
